import { useMemo, useState } from 'react';
import type {
  RoutingLoadEffectiveSettings,
  RoutingLoadScopeKind,
  RoutingLoadScopeSettingsRecord,
  UpdateRoutingLoadScopeSettingsRequest,
} from '@nadoutong/contracts';
import { useChannelModelsList } from '../../api/useChannelModels.ts';
import { useChannelsList } from '../../api/useChannels.ts';
import {
  useDeleteRoutingLoadScopeSettings,
  usePutRoutingLoadScopeSettings,
  useRoutingLoadScopeSettings,
  useRoutingLoadSettings,
} from '../../api/useRoutingLoad.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  Select,
} from '../../ui/index.ts';
import { RoutingLoadScopeEditor } from './RoutingLoadScopeEditor.tsx';
import type { ScopeKillSwitches } from './RoutingLoadScopeEditor.tsx';
import {
  LANE_ENABLE_FIELDS,
  LANE_ENABLE_LOAD_PATCH,
  namespaceFromSkuName,
  scopeRowOf,
} from './loadScopeFields.ts';

const KILL_COPY: Record<'l1' | 'l2' | 'l3' | 'l4', I18nKey> = {
  l1: 'tenant_routing.load.settings.kill_switch.l1',
  l2: 'tenant_routing.load.settings.kill_switch.l2',
  l3: 'tenant_routing.load.settings.kill_switch.l3',
  l4: 'tenant_routing.load.settings.kill_switch.l4',
};

const ENABLE_FIELD_LABEL: Record<(typeof LANE_ENABLE_FIELDS)[number], I18nKey> = {
  l2_enabled: 'tenant_routing.load.settings.field.l2_enabled',
  l3_enabled: 'tenant_routing.load.settings.field.l3_enabled',
  l3_inferred_caps_enabled: 'tenant_routing.load.settings.field.l3_inferred_caps_enabled',
  l4_enabled: 'tenant_routing.load.settings.field.l4_enabled',
  affinity_conversation_enabled: 'tenant_routing.load.settings.field.affinity_conversation_enabled',
  attempt_first_byte_timeout_ms: 'tenant_routing.load.settings.field.attempt_first_byte_timeout_ms',
};

export function RoutingLoadScopeView(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const settings = useRoutingLoadSettings();
  const scopeSettings = useRoutingLoadScopeSettings();
  const channels = useChannelsList();
  const skus = useChannelModelsList();
  const putScope = usePutRoutingLoadScopeSettings();
  const deleteScope = useDeleteRoutingLoadScopeSettings();
  const [enableLaneId, setEnableLaneId] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<{
    readonly kind: RoutingLoadScopeKind;
    readonly id: string;
  } | null>(null);
  const [addSkuOpen, setAddSkuOpen] = useState(false);
  const [skuQuery, setSkuQuery] = useState('');
  const [pickedSkuId, setPickedSkuId] = useState<string | undefined>(undefined);

  const data = settings.data;
  const items = scopeSettings.data?.items ?? [];
  const lanes = useMemo(
    () => [...(channels.data ?? [])].sort((a, b) => a.slug.localeCompare(b.slug)),
    [channels.data],
  );
  const skuRows = useMemo(
    () => items.filter((item) => item.scope_kind === 'sku'),
    [items],
  );
  const skuNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const sku of skus.data ?? []) {
      map.set(sku.sku_id, sku.sku_name);
    }
    return map;
  }, [skus.data]);
  const existingSkuIds = useMemo(() => new Set(skuRows.map((row) => row.scope_id)), [skuRows]);
  const addableSkus = useMemo(() => {
    const query = skuQuery.trim().toLowerCase();
    return [...(skus.data ?? [])]
      .filter((sku) => !existingSkuIds.has(sku.sku_id))
      .filter((sku) => {
        if (query.length === 0) return true;
        return sku.sku_name.toLowerCase().includes(query) || sku.sku_id.toLowerCase().includes(query);
      })
      .sort((a, b) => a.sku_name.localeCompare(b.sku_name));
  }, [skus.data, existingSkuIds, skuQuery]);

  if (settings.isPending || scopeSettings.isPending || channels.isPending) {
    return (
      <p className="m-0 text-body text-paper-low" data-testid="routing-load-scope-loading">
        {t('tenant_routing.load.scope.loading')}
      </p>
    );
  }
  if (data === undefined) {
    return (
      <p
        className="m-0 rounded bg-raised px-3 py-2 text-dense text-paper-low"
        data-testid="routing-load-scope-load-failed"
      >
        {t('tenant_routing.load.scope.load_failed')}
      </p>
    );
  }

  const kill: ScopeKillSwitches = {
    l2: data.l2_env_kill_switch,
    l3: data.l3_env_kill_switch,
    l4: data.l4_env_kill_switch,
    sessionRehome: data.session_rehome_env_kill_switch,
  };
  const saving = putScope.isPending || deleteScope.isPending;
  const laneEnableBlocked = kill.l2 || kill.l3 || kill.l4;

  function mutatePatch(
    kind: RoutingLoadScopeKind,
    scopeId: string,
    body: UpdateRoutingLoadScopeSettingsRequest,
    successKey: I18nKey,
  ): void {
    putScope.mutate(
      { kind, scopeId, body },
      {
        onSuccess: () => toast.success(t(successKey)),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  function mutateDelete(kind: RoutingLoadScopeKind, scopeId: string): void {
    deleteScope.mutate(
      { kind, scopeId },
      {
        onSuccess: () => toast.success(t('tenant_routing.load.scope.toast.restored')),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="routing-load-scope-view">
      <p className="m-0 text-dense leading-5 text-paper-low">{t('tenant_routing.load.scope.hint')}</p>
      {(kill.l2 || kill.l3 || kill.l4) ? (
        <div className="flex flex-col gap-2">
          {(['l2', 'l3', 'l4'] as const).map((layer) => (
            kill[layer] ? (
              <p
                key={layer}
                className="m-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense leading-5 text-warn"
                data-testid={`routing-load-scope-kill-${layer}`}
              >
                {t(KILL_COPY[layer])}
              </p>
            ) : null
          ))}
        </div>
      ) : null}

      <section className="flex flex-col gap-3" data-testid="routing-load-scope-lanes">
        <div>
          <h3 className="m-0 text-label text-paper">{t('tenant_routing.load.scope.lanes_title')}</h3>
          <p className="mt-1 mb-0 text-dense leading-5 text-paper-low">
            {t('tenant_routing.load.scope.lanes_hint')}
          </p>
        </div>
        {lanes.length === 0 ? (
          <EmptyState title={t('tenant_routing.load.scope.lanes_empty')} />
        ) : (
          lanes.map((channel) => {
            const row = scopeRowOf(items, 'namespace', channel.slug);
            return (
              <LaneOrSkuCard
                key={channel.id}
                kind="namespace"
                scopeId={channel.slug}
                title={channel.display_name}
                subtitle={channel.slug}
                row={row}
                skuValues={null}
                namespaceValues={row}
                globalEffective={data.effective}
                kill={kill}
                saving={saving}
                enableBlocked={laneEnableBlocked}
                onPatch={(body) => mutatePatch('namespace', channel.slug, body, 'tenant_routing.load.scope.toast.saved')}
                onEnable={() => setEnableLaneId(channel.slug)}
                onRestore={() => setRestoreTarget({ kind: 'namespace', id: channel.slug })}
              />
            );
          })
        )}
      </section>

      <section className="flex flex-col gap-3" data-testid="routing-load-scope-skus">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="m-0 text-label text-paper">{t('tenant_routing.load.scope.skus_title')}</h3>
            <p className="mt-1 mb-0 text-dense leading-5 text-paper-low">
              {t('tenant_routing.load.scope.skus_hint')}
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setSkuQuery('');
              setPickedSkuId(undefined);
              setAddSkuOpen(true);
            }}
            data-testid="routing-load-scope-add-sku"
          >
            {t('tenant_routing.load.scope.add_sku')}
          </Button>
        </div>
        {skuRows.length === 0 ? (
          <EmptyState
            title={t('tenant_routing.load.scope.skus_empty')}
            testId="routing-load-scope-skus-empty"
          />
        ) : (
          skuRows.map((row) => {
            const skuName = skuNameById.get(row.scope_id);
            const namespaceSlug = namespaceFromSkuName(skuName ?? null);
            const namespaceRow = namespaceSlug === null
              ? null
              : scopeRowOf(items, 'namespace', namespaceSlug);
            return (
              <LaneOrSkuCard
                key={`sku:${row.scope_id}`}
                kind="sku"
                scopeId={row.scope_id}
                title={skuName ?? row.scope_id}
                subtitle={row.scope_id}
                row={row}
                skuValues={row}
                namespaceValues={namespaceRow}
                globalEffective={data.effective}
                kill={kill}
                saving={saving}
                onPatch={(body) => mutatePatch('sku', row.scope_id, body, 'tenant_routing.load.scope.toast.saved')}
                onRestore={() => setRestoreTarget({ kind: 'sku', id: row.scope_id })}
              />
            );
          })
        )}
      </section>

      <ConfirmDialog
        open={enableLaneId !== null}
        onOpenChange={(open) => {
          if (!open) setEnableLaneId(null);
        }}
        title={t('tenant_routing.load.scope.enable_lane_title')}
        body={
          <div className="flex flex-col gap-2">
            <p className="m-0">{t('tenant_routing.load.scope.enable_lane_body')}</p>
            <ul className="m-0 list-disc pl-5" data-testid="routing-load-scope-enable-fields">
              {LANE_ENABLE_FIELDS.map((field) => (
                <li key={field}>{t(ENABLE_FIELD_LABEL[field])}</li>
              ))}
            </ul>
          </div>
        }
        confirmLabel={t('tenant_routing.load.scope.enable_lane_confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={putScope.isPending}
        onConfirm={() => {
          if (enableLaneId === null) return;
          const id = enableLaneId;
          mutatePatch('namespace', id, LANE_ENABLE_LOAD_PATCH, 'tenant_routing.load.scope.toast.enabled');
          setEnableLaneId(null);
        }}
        testId="routing-load-scope-enable-confirm"
      />

      <ConfirmDialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRestoreTarget(null);
        }}
        title={t('tenant_routing.load.scope.restore_title')}
        body={t('tenant_routing.load.scope.restore_body')}
        confirmLabel={t('tenant_routing.load.scope.restore_confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        tone="danger"
        loading={deleteScope.isPending}
        onConfirm={() => {
          if (restoreTarget === null) return;
          mutateDelete(restoreTarget.kind, restoreTarget.id);
          setRestoreTarget(null);
        }}
        testId="routing-load-scope-restore-confirm"
      />

      <Dialog
        open={addSkuOpen}
        onOpenChange={setAddSkuOpen}
        title={t('tenant_routing.load.scope.add_sku_title')}
        closeLabel={t('common.close')}
        testId="routing-load-scope-add-sku-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddSkuOpen(false)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={pickedSkuId === undefined || saving}
              loading={putScope.isPending}
              onClick={() => {
                if (pickedSkuId === undefined) return;
                mutatePatch('sku', pickedSkuId, {}, 'tenant_routing.load.scope.toast.sku_added');
                setAddSkuOpen(false);
              }}
              data-testid="routing-load-scope-add-sku-confirm"
            >
              {t('tenant_routing.load.scope.add_sku_confirm')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Input
            value={skuQuery}
            onChange={(event) => setSkuQuery(event.currentTarget.value)}
            placeholder={t('tenant_routing.load.scope.add_sku_search')}
            data-testid="routing-load-scope-add-sku-search"
          />
          {addableSkus.length === 0 ? (
            <p className="m-0 text-dense text-paper-low">{t('tenant_routing.load.scope.add_sku_none')}</p>
          ) : (
            <Select
              value={pickedSkuId}
              onValueChange={setPickedSkuId}
              placeholder={t('tenant_routing.load.scope.add_sku_pick')}
              options={addableSkus.map((sku) => ({ value: sku.sku_id, label: sku.sku_name }))}
              testId="routing-load-scope-add-sku-select"
            />
          )}
        </div>
      </Dialog>
    </div>
  );
}

function LaneOrSkuCard({
  kind,
  scopeId,
  title,
  subtitle,
  row,
  skuValues,
  namespaceValues,
  globalEffective,
  kill,
  saving,
  enableBlocked = false,
  onPatch,
  onEnable,
  onRestore,
}: {
  readonly kind: RoutingLoadScopeKind;
  readonly scopeId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly row: RoutingLoadScopeSettingsRecord | null;
  readonly skuValues: RoutingLoadScopeSettingsRecord | null;
  readonly namespaceValues: RoutingLoadScopeSettingsRecord | null;
  readonly globalEffective: RoutingLoadEffectiveSettings;
  readonly kill: ScopeKillSwitches;
  readonly saving: boolean;
  readonly enableBlocked?: boolean;
  readonly onPatch: (body: UpdateRoutingLoadScopeSettingsRequest) => void;
  readonly onEnable?: () => void;
  readonly onRestore: () => void;
}): JSX.Element {
  const t = useT();
  const prefix = `routing-load-scope-${kind}-${scopeId}`;
  return (
    <div
      className="flex flex-col gap-3 rounded border border-line bg-raised p-3"
      data-testid={prefix}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="m-0 text-label text-paper">{title}</h4>
          <p className="m-0 font-mono text-micro text-paper-low">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {kind === 'namespace' && onEnable !== undefined ? (
            <Button
              size="sm"
              disabled={saving || enableBlocked}
              onClick={onEnable}
              data-testid={`${prefix}-enable`}
            >
              {t('tenant_routing.load.scope.enable_lane')}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            disabled={saving || row === null}
            onClick={onRestore}
            data-testid={`${prefix}-restore`}
          >
            {t('tenant_routing.load.scope.restore')}
          </Button>
        </div>
      </div>
      <RoutingLoadScopeEditor
        kind={kind}
        scopeId={scopeId}
        skuValues={skuValues}
        namespaceValues={namespaceValues}
        globalEffective={globalEffective}
        kill={kill}
        saving={saving}
        onPatch={onPatch}
      />
    </div>
  );
}
